import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import FacebookGroupCTA from '../components/FacebookGroupCTA';
import NewsletterSection from '../components/NewsletterSection';
import ActivityTicker from '../components/ActivityTicker';
import PopularCategories from '../components/PopularCategories';
import { Search, MapPin, Briefcase, Calendar, ShoppingBag, TrendingUp, Star, Users, Tag, ArrowUpRight, ExternalLink, Plus, Building2, HandHelping, CheckCircle, ChevronUp, Sparkles, Heart, MessageSquare, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ListingCard from '../components/ListingCard';
import SignUpPromptModal from '../components/SignUpPromptModal';
import { toast } from 'sonner';
import { HomeSEO } from '../components/SEO';
import useReveal from '../hooks/useReveal';
import { heroImage } from '../utils/cloudinaryOptimize';
import { TopBannerAd, FooterAd } from '../components/AdBanner';
import PersonalPromoBanner from '../components/PersonalPromoBanner';

const API = '/api';

const ESSEX_TOWNS = ['Basildon','Benfleet','Billericay','Braintree','Brentwood','Brightlingsea','Burnham-on-Crouch','Canvey Island','Chelmsford','Clacton-on-Sea','Colchester','Corringham','Epping','Grays','Great Dunmow','Halstead','Harlow','Harwich','Ingatestone','Leigh-on-Sea','Loughton','Maldon','Ongar','Rayleigh','Romford','Saffron Walden','Shoeburyness','Southend-on-Sea','Stanford-le-Hope','Tiptree','Wickford','Witham'];

const BG_PRESETS = [
  { id: 'default', gradient: 'linear-gradient(135deg, #0F2C3E 0%, #1a4355 50%, #4A6741 100%)' },
  { id: 'ocean', gradient: 'linear-gradient(135deg, #0c2340 0%, #1565C0 50%, #0D47A1 100%)' },
  { id: 'sunset', gradient: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #e94560 100%)' },
  { id: 'forest', gradient: 'linear-gradient(135deg, #1B2A1B 0%, #2E4830 50%, #1a3a1a 100%)' },
  { id: 'midnight', gradient: 'linear-gradient(135deg, #0a0a23 0%, #1a1a3e 50%, #2d1b69 100%)' },
  { id: 'slate', gradient: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)' },
];

const getHeroStyle = (config) => {
  if (!config) return undefined;
  if (config.bg_type === 'image' && config.bg_image_url) {
    return {
      backgroundImage: `url(${heroImage(config.bg_image_url)})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    };
  }
  if (config.bg_preset === 'custom' && config.bg_custom_gradient) return { background: config.bg_custom_gradient };
  const preset = BG_PRESETS.find(p => p.id === config.bg_preset);
  return preset ? { background: preset.gradient } : undefined;
};

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const heroSearchRef = useRef(null);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [watchlist, setWatchlist] = useState(new Set());
  const [watchlistLoading, setWatchlistLoading] = useState(new Set());
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [stats, setStats] = useState({ businesses: 0, events: 0, jobs: 0, classifieds: 0, reviews: 0, users: 0, signups_week: 0 });
  const [popularPages, setPopularPages] = useState([]);
  const [showMoreSeo, setShowMoreSeo] = useState(false);
  const [recentCommunityPosts, setRecentCommunityPosts] = useState([]);
  const [heroConfig, setHeroConfig] = useState(() => {
    try {
      const cached = localStorage.getItem('heroConfig');
      if (cached) return JSON.parse(cached);
    } catch {}
    return {
      layout: 'centered',
      bg_type: 'gradient',
      bg_preset: 'default',
      badge_text: "Essex's Premier Local Directory",
      heading_prefix: 'Local Businesses, Jobs',
      heading_highlight: 'Essex',
      subtext: 'The trusted directory for Essex — 2,300+ verified listings.',
      cta_text: "Get Started — It's Free",
      cta_subtext: 'All listing types are completely free to create — no credit card required',
    };
  });
  const [quickTitle, setQuickTitle] = useState('');
  const [quickTown, setQuickTown] = useState('');
  const [heroSearchWhere, setHeroSearchWhere] = useState('');
  const [cardBusinessName, setCardBusinessName] = useState('');
  const [cardPostcode, setCardPostcode] = useState('');
  const revealRef = useReveal();

  // Close hero search suggestions when tapping outside the search form,
  // but NOT when the mobile keyboard dismisses (which only fires blur).
  useEffect(() => {
    const handleOutside = (e) => {
      if (heroSearchRef.current && !heroSearchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, []);


  const handleQuickRequest = () => {
    const params = new URLSearchParams();
    if (quickTitle.trim()) params.set('title', quickTitle.trim());
    if (quickTown) params.set('town', quickTown);
    const qs = params.toString();
    if (!user) {
      navigate(`/signup?redirect=/create-request${qs ? `&${qs}` : ''}`);
    } else {
      navigate(`/create-request${qs ? `?${qs}` : ''}`);
    }
  };

  useEffect(() => {
    fetchFeaturedListings();
    fetchStats();
    fetchPopularPages();
    // Fetch recent community posts for the community hero variant
    axios.get(`${API}/community/posts?limit=3&sort=newest`).then(res => {
      const posts = res.data?.posts || res.data || [];
      setRecentCommunityPosts(posts.slice(0, 3));
    }).catch(() => {});
    axios.get(`${API}/public/hero-config`).then(res => {
      if (!res.data) return;
      const newConfig = res.data;
      const apply = () => {
        setHeroConfig(newConfig);
        try { localStorage.setItem('heroConfig', JSON.stringify(newConfig)); } catch {}
      };
      // If the new variant has a background image, preload it before swapping
      if (newConfig.bg_type === 'image' && newConfig.bg_image_url) {
        const img = new Image();
        img.onload = apply;
        img.onerror = apply;
        img.src = newConfig.bg_image_url;
      } else {
        apply();
      }
    }).catch(() => {});
    // Fetch Essex towns for the search_card WHERE dropdown
    axios.get(`${API}/towns`).then(res => setHeroTowns(res.data?.towns || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery.length >= 2) {
        fetchSuggestions();
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const fetchSuggestions = async () => {
    const cacheKey = `search_cache_${searchQuery.toLowerCase().trim()}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < 5 * 60 * 1000) { // 5 min TTL
          setSearchSuggestions(data);
          setShowSuggestions(true);
          return;
        }
      } catch {}
    }
    try {
      const response = await axios.get(`${API}/listings/search/suggestions?q=${encodeURIComponent(searchQuery)}`);
      setSearchSuggestions(Array.isArray(response.data) ? response.data : []);
      setShowSuggestions(true);
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ data: response.data, ts: Date.now() })); } catch {}
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const fetchFeaturedListings = async () => {
    try {
      // Use the new endpoint that returns an even mix of featured listings
      const response = await axios.get(`${API}/listings/featured/mix?limit_per_category=3`);
      setFeaturedListings(response.data);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
      // Fallback to regular listings if featured endpoint fails
      try {
        const fallback = await axios.get(`${API}/listings?limit=6`);
        setFeaturedListings(fallback.data);
      } catch (err) {
        console.error('Fallback also failed:', err);
      }
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/homepage`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Value-line count: total listings from admin-dashboard-equivalent count
  // (all listings regardless of status), so the homepage boast matches what
  // the operator sees under "Total Listings" in the admin. Rounded up to
  // nearest 100. Falls back to per-type sum, then 2,300 for cold-start.
  const totalActiveListings = (stats.businesses || 0) + (stats.jobs || 0) + (stats.events || 0) + (stats.classifieds || 0);
  const totalListings = stats.total_listings || totalActiveListings;
  const roundedListings = totalListings >= 200 ? Math.ceil(totalListings / 100) * 100 : 2300;
  const roundedMembers = (stats.users || 0) >= 200 ? Math.ceil((stats.users || 0) / 100) * 100 : 700;
  const listingsCountLabel = `${roundedListings.toLocaleString('en-GB')}+ verified listings`;
  const heroValueLine = `The trusted directory for Essex — ${listingsCountLabel}.`;

  // Token substitution for hero text fields. Admins can drop {listings_count}
  // or {members_count} into any hero copy field via the admin panel and it
  // resolves at render time to a live count rounded up to the nearest 100
  // (so the number always feels credible, never sub-100).
  const applyHeroTokens = (text) => {
    if (!text) return text;
    return text
      .replace(/\{listings_count\}/g, `${roundedListings.toLocaleString('en-GB')}+`)
      .replace(/\{members_count\}/g, `${roundedMembers.toLocaleString('en-GB')}+`);
  };
  const heroSubtext = applyHeroTokens(heroConfig?.subtext) || heroValueLine;
  const heroCtaSubtext = applyHeroTokens(heroConfig?.cta_subtext);

  const [heroTowns, setHeroTowns] = useState([]);

  const fetchPopularPages = async () => {
    try {
      const response = await axios.get(`${API}/seo/popular-pages?limit=12`);
      setPopularPages(response.data.pages || []);
    } catch (error) {
      console.error('Failed to fetch popular pages:', error);
    }
  };

  // Fetch watchlist for logged-in users
  useEffect(() => {
    if (!user) return;
    if (!user) return;
    axios.get(`${API}/watchlist`)
      .then(res => setWatchlist(new Set((res.data || []).map(w => w.listing_id))))
      .catch(() => {});
  }, [user]);

  const toggleWatchlist = async (listingId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      setShowSignUpModal(true);
      return;
    }
    setWatchlistLoading(prev => new Set([...prev, listingId]));
    try {
      if (watchlist.has(listingId)) {
        await axios.delete(`${API}/watchlist/${listingId}`);
        setWatchlist(prev => { const n = new Set(prev); n.delete(listingId); return n; });
      } else {
        await axios.post(`${API}/watchlist/${listingId}`, {});
        setWatchlist(prev => new Set([...prev, listingId]));
      }
    } catch {}
    setWatchlistLoading(prev => { const n = new Set(prev); n.delete(listingId); return n; });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Save to recent searches (max 5)
      try {
        const stored = JSON.parse(localStorage.getItem('de_recent_searches') || '[]');
        const updated = [searchQuery.trim(), ...stored.filter(s => s.toLowerCase() !== searchQuery.trim().toLowerCase())].slice(0, 5);
        localStorage.setItem('de_recent_searches', JSON.stringify(updated));
      } catch {}
      axios.post(`${API}/track-search`, { query: searchQuery.trim(), type: 'completed', page: 'home' }).catch(() => {});
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSuggestions(false);
    }
  };

  const recentSearches = (() => {
    try { return JSON.parse(localStorage.getItem('de_recent_searches') || '[]'); } catch { return []; }
  })();

  const clearRecentSearches = () => {
    localStorage.removeItem('de_recent_searches');
  };

  const handleSuggestionClick = (listingId) => {
    navigate(`/listing/${listingId}`);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  const categories = [
    { name: 'Businesses', icon: ShoppingBag, type: 'business', bgColor: 'bg-blue-50', iconColor: 'text-blue-600', borderColor: 'border-blue-200', hoverBg: 'hover:bg-blue-100', count: stats.businesses, link: '/business' },
    { name: 'Jobs', icon: Briefcase, type: 'job', bgColor: 'bg-green-50', iconColor: 'text-green-600', borderColor: 'border-green-200', hoverBg: 'hover:bg-green-100', count: stats.jobs, link: '/jobs' },
    { name: 'Events', icon: Calendar, type: 'event', bgColor: 'bg-purple-50', iconColor: 'text-purple-600', borderColor: 'border-purple-200', hoverBg: 'hover:bg-purple-100', count: stats.events, link: '/events' },
    { name: 'Marketplace', icon: Tag, type: 'classified', bgColor: 'bg-amber-50', iconColor: 'text-amber-600', borderColor: 'border-amber-200', hoverBg: 'hover:bg-amber-100', count: stats.classifieds, link: '/marketplace' },
  ];

  return (
    <div ref={revealRef}>
      {/* SEO Meta Tags with Organization and WebSite Schema */}
      <HomeSEO />

      {/* Personal welcome code banner — auth-only. Sits above the hero so a
          logged-in user with an active offer sees their countdown first. */}
      {user && <PersonalPromoBanner />}
      
      {/* Preload hero background image for LCP */}
      {heroConfig?.bg_type === 'image' && heroConfig?.bg_image_url && (
        <link rel="preload" as="image" href={heroConfig.bg_image_url} fetchpriority="high" />
      )}
      
      <section
        className="hero-section relative overflow-visible"
        data-testid="hero-section"
        style={getHeroStyle(heroConfig)}
      >
        {/* Dark overlay for image backgrounds (skip for cinematic — it handles its own) */}
        {heroConfig?.bg_type === 'image' && heroConfig?.bg_image_url && heroConfig?.layout !== 'cinematic' && heroConfig?.layout !== 'community' && heroConfig?.layout !== 'search_card' && (
          <div className="absolute inset-0 bg-black/55" />
        )}
        {/* Cinematic gradient overlay for community layout when enabled */}
        {heroConfig?.bg_type === 'image' && heroConfig?.bg_image_url && (heroConfig?.layout === 'community' || heroConfig?.layout === 'search_card') && (
          heroConfig?.use_gradient_overlay !== false ? (
            <>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2C3E] via-[#0F2C3E]/75 to-[#0F2C3E]/15 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0F2C3E]/60 via-transparent to-transparent pointer-events-none" />
            </>
          ) : (
            <div className="absolute inset-0 bg-black/55" />
          )
        )}

        {heroConfig?.layout === 'split' ? (
          /* ═══════════════════════════════════════════
             SPLIT LAYOUT — New Design 2026
          ═══════════════════════════════════════════ */
          <>
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute -top-40 -right-40 w-[700px] h-[700px] bg-[#D4A017]/10 rounded-full blur-[130px]" />
              <div className="absolute bottom-0 -left-20 w-[500px] h-[500px] bg-[#4A6741]/12 rounded-full blur-[110px]" />
            </div>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                <div className="col-span-1 lg:col-span-7 flex flex-col gap-6">
                  <div>
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white/90 text-sm font-medium" data-testid="hero-badge">
                      <Sparkles className="h-3.5 w-3.5 text-[#D4A017]" />
                      {heroConfig?.badge_text || "Essex's Premier Local Directory"}
                    </span>
                  </div>
                  <h1 className="hero-heading text-5xl md:text-6xl lg:text-7xl text-white leading-[1.08]">
                    {heroConfig?.heading_prefix || 'Local Businesses, Jobs & Events in'}{' '}
                    <span className="text-[#D4A017] italic">{heroConfig?.heading_highlight || 'Essex'}</span>
                  </h1>
                  <p className="text-lg text-white/80 max-w-xl leading-relaxed font-light">
                    {heroSubtext}
                  </p>
                  <form onSubmit={handleSearch} className="relative z-20 max-w-xl" data-testid="search-form" ref={heroSearchRef}>
                    <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 flex items-center gap-2 p-2 transition-all duration-300 focus-within:shadow-[0_0_0_3px_rgba(212,160,23,0.3)]">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                      <Input
                        type="text"
                        placeholder="Search businesses & more"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setShowSuggestions(true)}
                        className="flex-1 pl-10 border-0 focus-visible:ring-0 text-base h-12 bg-transparent text-slate-800 placeholder:text-slate-400"
                        data-testid="search-input"
                        aria-label="Search Discover Essex"
                      />
                      <Button type="submit" className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 text-white px-3 sm:px-6 h-10 rounded-xl shrink-0 font-medium" data-testid="search-btn"><Search className="h-4 w-4 sm:hidden" /><span className="hidden sm:inline">Search</span></Button>
                    </div>
                    {showSuggestions && searchSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border z-[100] overflow-hidden max-h-80 overflow-y-auto">
                        {searchSuggestions.map((listing) => (
                          <button key={listing.id} onClick={() => handleSuggestionClick(listing.id)} className="w-full px-4 py-3 hover:bg-slate-50 text-left flex items-center gap-3 transition-colors">
                            <MapPin className="h-4 w-4 text-[#0F2C3E]" />
                            <div className="flex-1"><div className="font-medium">{listing.title}</div><div className="text-sm text-slate-500">{listing.town} &bull; {listing.category}</div></div>
                            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">{listing.listing_type === 'classified' ? 'Marketplace' : listing.listing_type}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {showSuggestions && searchSuggestions.length === 0 && !searchQuery && recentSearches.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border z-[100] overflow-hidden" data-testid="recent-searches">
                        <div className="px-4 py-2 flex items-center justify-between border-b">
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Recent searches</span>
                          <button onClick={(e) => { e.preventDefault(); clearRecentSearches(); setShowSuggestions(false); }} className="text-xs text-slate-400 hover:text-red-500 transition-colors">Clear</button>
                        </div>
                        {recentSearches.map((term, i) => (
                          <button key={i} onClick={() => { setSearchQuery(term); navigate(`/search?q=${encodeURIComponent(term)}`); setShowSuggestions(false); }} className="w-full px-4 py-3 hover:bg-slate-50 text-left flex items-center gap-3 transition-colors">
                            <Clock className="h-4 w-4 text-slate-400" />
                            <span className="text-sm text-slate-700">{term}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </form>
                  <PopularCategories tone="dark" />
                  <div className="flex flex-wrap gap-2 items-center" data-testid="hero-category-pills">
                    <span className="text-white/45 text-xs font-medium uppercase tracking-wide">Browse:</span>
                    {[
                      { label: 'Businesses', icon: Building2, link: '/business' },
                      { label: 'Jobs', icon: Briefcase, link: '/jobs' },
                      { label: 'Events', icon: Calendar, link: '/events' },
                      { label: 'Marketplace', icon: Tag, link: '/marketplace' },
                      { label: 'Find a Trade', icon: HandHelping, link: '/find-a-trade' },
                    ].map(({ label, icon: Icon, link }) => (
                      <button key={label} onClick={() => navigate(link)} className="group flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 hover:border-[#D4A017]/40 transition-all cursor-pointer">
                        <Icon className="h-3.5 w-3.5 text-white/55 group-hover:text-[#D4A017] transition-colors" />
                        <span className="text-xs text-white/75 group-hover:text-white transition-colors">{label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 items-start" data-testid="hero-free-listing-cta">
                    <button onClick={() => { axios.post(`${API}/track-click`, { element: 'hero_get_started', headline: 'Hero — Get Started Free', subtext: 'Homepage', cta_text: heroConfig?.cta_text || "Get Started — It's Free", cta_url: '/create-listing' }).catch(() => {}); navigate(user ? '/create-listing' : '/signup'); }} className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#F5B728] text-[#0F2C3E] px-7 py-3.5 rounded-xl font-bold text-base shadow-[0_0_24px_rgba(212,160,23,0.35)] hover:shadow-[0_0_36px_rgba(212,160,23,0.5)] transition-all hover:-translate-y-0.5" data-testid="hero-get-started-btn">
                      {heroConfig?.cta_text || "Get Started — It's Free"}<ArrowUpRight className="h-4 w-4" />
                    </button>
                    <button onClick={() => navigate('/search')} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-base text-white border border-white/20 hover:bg-white/10 transition-all" data-testid="hero-browse-btn">Browse Listings</button>
                  </div>
                  <p className="text-white/45 text-xs flex items-center gap-1.5">
                    <CheckCircle className="h-3.5 w-3.5 text-[#4A6741]" />
                    {heroCtaSubtext || 'No credit card required · 100% free · All listing types included'}
                  </p>
                </div>
                <div className="hidden lg:block col-span-5">
                  <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-3xl shadow-2xl overflow-hidden hover:border-white/20 transition-all duration-500">
                    <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#D4A017]/15 rounded-full blur-[90px] pointer-events-none" />
                    <div className="relative z-10">
                      <div className="inline-flex items-center gap-2 bg-[#4A6741]/20 border border-[#4A6741]/30 text-emerald-300 px-3 py-1 rounded-full text-xs font-bold mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live Listings
                      </div>
                      <div className="grid grid-cols-2 gap-6 mb-6">
                        {[
                          { label: 'Businesses', value: stats.businesses, icon: Building2, color: 'text-amber-300' },
                          { label: 'Jobs', value: stats.jobs, icon: Briefcase, color: 'text-emerald-300' },
                          { label: 'Events', value: stats.events, icon: Calendar, color: 'text-blue-300' },
                          { label: 'Marketplace', value: stats.classifieds, icon: Tag, color: 'text-purple-300' },
                        ].map(({ label, value, icon: Icon, color }) => (
                          <div key={label} className="flex flex-col gap-1">
                            <Icon className={`h-4 w-4 ${color} mb-0.5`} />
                            <span className="hero-heading text-3xl font-bold text-white">{value > 0 ? value.toLocaleString() : '—'}</span>
                            <span className="text-xs text-white/50 uppercase tracking-wider">{label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-white/10 pt-5">
                        <p className="text-white/55 text-sm mb-3">Essex businesses growing every day</p>
                        <button onClick={() => navigate(user ? '/create-listing' : '/signup')} className="w-full py-2.5 rounded-xl border border-[#D4A017]/40 text-[#D4A017] text-sm font-semibold hover:bg-[#D4A017]/10 transition-all" data-testid="hero-stats-list-btn">+ List your business free</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : heroConfig?.layout === 'cinematic' ? (
          /* ═══════════════════════════════════════════
             CINEMATIC LAYOUT — Full-bleed photo
          ═══════════════════════════════════════════ */
          <>
            {/* Gradient: transparent at top → deep navy at bottom */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2C3E] via-[#0F2C3E]/75 to-[#0F2C3E]/15 pointer-events-none" />
            {/* Side vignette */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F2C3E]/60 via-transparent to-transparent pointer-events-none" />

            <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10 flex flex-col py-28 md:py-36">

              {/* Top — badge */}
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white/80 text-xs font-medium" data-testid="hero-badge">
                  <Sparkles className="h-3 w-3 text-[#D4A017]" />
                  {heroConfig?.badge_text || "Essex's Premier Local Directory"}
                </span>
              </div>

              {/* Bottom — main content anchored to base */}
              <div className="max-w-2xl">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight mb-5" data-testid="hero-heading">
                  {heroConfig?.heading_prefix || 'Local Businesses, Jobs & Events in'}{' '}
                  <span className="text-[#D4A017]">{heroConfig?.heading_highlight || 'Essex'}</span>
                </h1>

                <p className="text-white/70 text-lg mb-6 max-w-md leading-relaxed">
                  {heroSubtext}
                </p>

                {/* Search */}
                <form onSubmit={handleSearch} className="relative z-20 max-w-lg mb-5" data-testid="search-form" ref={heroSearchRef}>
                  <div className="flex items-center bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden pl-4 pr-1.5 py-1.5 gap-2 focus-within:ring-2 focus-within:ring-[#D4A017]/50 transition-all">
                    <Search className="h-4 w-4 text-slate-400 shrink-0" />
                    <Input
                      type="text"
                      placeholder="Search businesses & more"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setShowSuggestions(true)}
                      className="flex-1 border-0 focus-visible:ring-0 text-[16px] h-10 bg-transparent text-slate-800 placeholder:text-slate-400 p-0"
                      data-testid="search-input"
                      aria-label="Search Discover Essex"
                    />
                    <Button type="submit" className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 text-white h-9 px-5 rounded-xl text-sm shrink-0" data-testid="search-btn">Search</Button>
                  </div>
                  {showSuggestions && searchSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border z-[100] overflow-hidden max-h-80 overflow-y-auto">
                      {searchSuggestions.map((listing) => (
                        <button key={listing.id} onClick={() => handleSuggestionClick(listing.id)} className="w-full px-4 py-3 hover:bg-slate-50 text-left flex items-center gap-3 transition-colors">
                          <MapPin className="h-4 w-4 text-[#0F2C3E]" />
                          <div className="flex-1"><div className="font-medium text-sm">{listing.title}</div><div className="text-xs text-slate-500">{listing.town} • {listing.category}</div></div>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{listing.listing_type === 'classified' ? 'Marketplace' : listing.listing_type}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </form>
                <PopularCategories tone="dark" />

                {/* CTA row */}
                <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="hero-free-listing-cta">
                  <button
                    onClick={() => { axios.post(`${API}/track-click`, { element: 'hero_get_started', cta_text: heroConfig?.cta_text || "Get Started — It's Free", cta_url: '/create-listing' }).catch(() => {}); navigate(user ? '/create-listing' : '/signup'); }}
                    className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#F5B728] text-[#0F2C3E] px-6 py-3 rounded-xl font-bold text-sm shadow-[0_8px_24px_rgba(212,160,23,0.4)] hover:shadow-[0_8px_32px_rgba(212,160,23,0.55)] transition-all hover:-translate-y-0.5"
                    data-testid="hero-get-started-btn"
                  >
                    {heroConfig?.cta_text || "Get Started — It's Free"} <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => navigate('/search')} className="text-white/70 hover:text-white text-sm font-medium transition-colors flex items-center gap-1 data-testid" data-testid="hero-browse-btn">
                    Browse all listings <ArrowUpRight className="h-3 w-3" />
                  </button>
                </div>

                {/* Live stats bar */}
                {heroConfig?.show_stats_bar !== false && (
                <div className="flex flex-wrap gap-6 pt-4 border-t border-white/15">
                  {[
                    { label: 'Businesses', value: stats.businesses, icon: Building2 },
                    { label: 'Jobs', value: stats.jobs, icon: Briefcase },
                    { label: 'Events', value: stats.events, icon: Calendar },
                    { label: 'Marketplace', value: stats.classifieds, icon: Tag },
                  ].map(({ label, value, icon: Icon }) => (
                    <div key={label} className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-[#D4A017]" />
                      <span className="text-white font-bold text-sm">{value > 0 ? value.toLocaleString() : '0'}</span>
                      <span className="text-white/45 text-xs">{label}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5 ml-auto">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-white/40 text-xs">Live</span>
                  </div>
                </div>
                )}
              </div>
            </div>
          </>
        ) : heroConfig?.layout === 'bento' ? (
          /* ═══════════════════════════════════════════
             BENTO LAYOUT — Category Grid Design
          ═══════════════════════════════════════════ */
          <>
            <div className="absolute inset-0 opacity-[0.07]">
              <div className="absolute top-10 right-10 w-80 h-80 bg-[#D4A017] rounded-full blur-3xl" />
              <div className="absolute bottom-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
            </div>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-24 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

                {/* Left — headline + search + CTA */}
                <div className="flex flex-col gap-5">
                  <span className="inline-flex items-center gap-2 self-start px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/80 text-xs font-medium" data-testid="hero-badge">
                    <Sparkles className="h-3 w-3 text-[#D4A017]" />
                    {heroConfig?.badge_text || "Essex's Premier Local Directory"}
                  </span>

                  <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight" data-testid="hero-heading">
                    {heroConfig?.heading_prefix || 'Local Businesses, Jobs & Events in'}{' '}
                    <span className="relative">
                      <span className="text-[#D4A017]">{heroConfig?.heading_highlight || 'Essex'}</span>
                      <svg className="absolute -bottom-1 left-0 w-full" height="6" viewBox="0 0 100 6" preserveAspectRatio="none">
                        <path d="M0 5 Q25 1 50 4 Q75 7 100 3" stroke="#D4A017" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.6"/>
                      </svg>
                    </span>
                  </h1>

                  <p className="text-white/75 text-base leading-relaxed max-w-sm">
                    {heroSubtext}
                  </p>

                  <form onSubmit={handleSearch} className="relative z-20" data-testid="search-form" ref={heroSearchRef}>
                    <div className="flex items-center bg-white rounded-2xl shadow-xl overflow-hidden pr-1.5 py-1.5 pl-4 gap-2 focus-within:ring-2 focus-within:ring-[#D4A017]/40 transition-all">
                      <Search className="h-4 w-4 text-slate-400 shrink-0" />
                      <Input
                        type="text"
                        placeholder="Search businesses & more"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setShowSuggestions(true)}
                        className="flex-1 border-0 focus-visible:ring-0 text-[16px] h-10 bg-transparent text-slate-800 placeholder:text-slate-400 p-0"
                        data-testid="search-input"
                        aria-label="Search Discover Essex"
                      />
                      <Button type="submit" className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 text-white h-9 px-5 rounded-xl text-sm shrink-0" data-testid="search-btn">Search</Button>
                    </div>
                    {showSuggestions && searchSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border z-[100] overflow-hidden max-h-80 overflow-y-auto">
                        {searchSuggestions.map((listing) => (
                          <button key={listing.id} onClick={() => handleSuggestionClick(listing.id)} className="w-full px-4 py-3 hover:bg-slate-50 text-left flex items-center gap-3 transition-colors">
                            <MapPin className="h-4 w-4 text-[#0F2C3E]" />
                            <div className="flex-1"><div className="font-medium text-sm">{listing.title}</div><div className="text-xs text-slate-500">{listing.town} • {listing.category}</div></div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{listing.listing_type === 'classified' ? 'Marketplace' : listing.listing_type}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </form>
                  <PopularCategories tone="dark" />

                  <div className="flex items-center gap-3" data-testid="hero-free-listing-cta">
                    <button
                      onClick={() => { axios.post(`${API}/track-click`, { element: 'hero_get_started', cta_text: heroConfig?.cta_text || "Get Started — It's Free", cta_url: '/create-listing' }).catch(() => {}); navigate(user ? '/create-listing' : '/signup'); }}
                      className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#F5B728] text-[#0F2C3E] px-6 py-3 rounded-xl font-bold text-sm shadow-[0_0_20px_rgba(212,160,23,0.3)] hover:shadow-[0_0_30px_rgba(212,160,23,0.45)] transition-all hover:-translate-y-0.5"
                      data-testid="hero-get-started-btn"
                    >
                      {heroConfig?.cta_text || "Get Started — It's Free"} <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                    <p className="text-white/45 text-xs">{heroCtaSubtext || '100% free, no card needed'}</p>
                  </div>
                </div>

                {/* Right — 2×2 interactive category bento tiles */}
                <div className="grid grid-cols-2 gap-3" data-testid="hero-category-bento">
                  {[
                    { label: 'Businesses', icon: Building2, count: stats.businesses, link: '/business', accent: '#D4A017', bg: 'rgba(212,160,23,0.08)', border: 'rgba(212,160,23,0.2)', desc: 'Local trades & services' },
                    { label: 'Jobs', icon: Briefcase, count: stats.jobs, link: '/jobs', accent: '#4ade80', bg: 'rgba(74,222,128,0.08)', border: 'rgba(74,222,128,0.2)', desc: 'Local opportunities' },
                    { label: 'Events', icon: Calendar, count: stats.events, link: '/events', accent: '#60a5fa', bg: 'rgba(96,165,250,0.08)', border: 'rgba(96,165,250,0.2)', desc: "What's on in Essex" },
                    { label: 'Marketplace', icon: Tag, count: stats.classifieds, link: '/marketplace', accent: '#c084fc', bg: 'rgba(192,132,252,0.08)', border: 'rgba(192,132,252,0.2)', desc: 'Buy & sell locally' },
                  ].map(({ label, icon: Icon, count, link, accent, bg, border, desc }) => (
                    <button
                      key={label}
                      onClick={() => navigate(link)}
                      className="group relative p-5 rounded-2xl text-left transition-all duration-200 hover:-translate-y-1 hover:shadow-xl"
                      style={{ background: bg, border: `1px solid ${border}` }}
                      data-testid={`bento-tile-${label.toLowerCase()}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="p-2 rounded-xl" style={{ background: `${accent}20` }}>
                          <Icon className="h-5 w-5" style={{ color: accent }} />
                        </div>
                        <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-white/70 transition-colors" />
                      </div>
                      <div className="font-bold text-2xl text-white mb-0.5">{count > 0 ? count.toLocaleString() : '—'}</div>
                      <div className="text-sm font-semibold text-white/80">{label}</div>
                      <div className="text-xs text-white/45 mt-0.5">{desc}</div>
                    </button>
                  ))}
                </div>

              </div>
            </div>
          </>
        ) : heroConfig?.layout === 'community' ? (
          /* ═══════════════════════════════════════════
             COMMUNITY LAYOUT — Hub + Directory Hybrid
          ═══════════════════════════════════════════ */
          <>
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute -top-40 -right-40 w-[700px] h-[700px] bg-[#D4A017]/10 rounded-full blur-[130px]" />
              <div className="absolute bottom-0 -left-20 w-[500px] h-[500px] bg-[#4A6741]/12 rounded-full blur-[110px]" />
            </div>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-24 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                {/* Left — main headline + search */}
                <div className="col-span-1 lg:col-span-7 flex flex-col gap-5">
                  <span className="inline-flex items-center gap-2 self-start px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white/90 text-sm font-medium" data-testid="hero-badge">
                    <Sparkles className="h-3.5 w-3.5 text-[#D4A017]" />
                    {heroConfig?.badge_text || "More than just a directory"}
                  </span>
                  <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight" data-testid="hero-heading">
                    {heroConfig?.heading_prefix || 'Local Businesses, Jobs & Events in'}{' '}
                    <span className="text-[#D4A017]">{heroConfig?.heading_highlight || 'Essex'}</span>
                  </h1>
                  <p className="text-white/70 text-lg max-w-md leading-relaxed">
                    {applyHeroTokens(heroConfig?.subtext) || 'Search local businesses, find events and jobs — then hear what your Essex community is talking about right now.'}
                  </p>
                  <form onSubmit={handleSearch} className="relative z-20 max-w-xl" data-testid="search-form" ref={heroSearchRef}>
                    <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 flex items-center gap-2 p-2 transition-all duration-300 focus-within:shadow-[0_0_0_3px_rgba(212,160,23,0.3)]">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                      <Input
                        type="text"
                        placeholder="Search businesses & more"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setShowSuggestions(true)}
                        className="flex-1 pl-10 border-0 focus-visible:ring-0 text-base h-12 bg-transparent text-slate-800 placeholder:text-slate-400"
                        data-testid="search-input"
                        aria-label="Search Discover Essex"
                      />
                      <Button type="submit" className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 text-white px-3 sm:px-6 h-10 rounded-xl shrink-0 font-medium" data-testid="search-btn"><Search className="h-4 w-4 sm:hidden" /><span className="hidden sm:inline">Search</span></Button>
                    </div>
                    {showSuggestions && searchSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border z-[100] overflow-hidden max-h-80 overflow-y-auto">
                        {searchSuggestions.map((listing) => (
                          <button key={listing.id} onClick={() => handleSuggestionClick(listing.id)} className="w-full px-4 py-3 hover:bg-slate-50 text-left flex items-center gap-3 transition-colors">
                            <MapPin className="h-4 w-4 text-[#0F2C3E]" />
                            <div className="flex-1"><div className="font-medium">{listing.title}</div><div className="text-sm text-slate-500">{listing.town} • {listing.category}</div></div>
                            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">{listing.listing_type === 'classified' ? 'Marketplace' : listing.listing_type}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </form>
                  <PopularCategories tone="dark" />
                  {/* Dual CTAs */}
                  <div className="flex flex-col sm:flex-row gap-3 items-start" data-testid="hero-free-listing-cta">
                    <button onClick={() => { axios.post(`${API}/track-click`, { element: 'hero_get_started', cta_text: heroConfig?.cta_text || "Get Started — It's Free", cta_url: '/create-listing' }).catch(() => {}); navigate(user ? '/create-listing' : '/signup'); }} className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#F5B728] text-[#0F2C3E] px-7 py-3.5 rounded-xl font-bold text-base shadow-[0_0_24px_rgba(212,160,23,0.35)] hover:shadow-[0_0_36px_rgba(212,160,23,0.5)] transition-all hover:-translate-y-0.5" data-testid="hero-get-started-btn">
                      {heroConfig?.cta_text || "List for Free"}<ArrowUpRight className="h-4 w-4" />
                    </button>
                    <button onClick={() => navigate('/community')} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-base text-white border border-white/20 hover:bg-white/10 transition-all" data-testid="hero-community-btn">
                      <MessageSquare className="h-4 w-4" /> Join the Conversation
                    </button>
                  </div>
                  <p className="text-white/45 text-xs flex items-center gap-1.5">
                    <CheckCircle className="h-3.5 w-3.5 text-[#4A6741]" />
                    {heroCtaSubtext || '100% free directory + community hub for all Essex residents'}
                  </p>
                </div>

                {/* Right — Community Pulse Card with REAL posts */}
                <div className="hidden lg:block col-span-5">
                  <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden hover:border-white/20 transition-all duration-500">
                    <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#D4A017]/15 rounded-full blur-[90px] pointer-events-none" />
                    <div className="relative z-10 p-7">
                      <div className="flex items-center gap-2 mb-5">
                        <div className="w-8 h-8 rounded-lg bg-[#D4A017]/20 flex items-center justify-center">
                          <Users className="h-4 w-4 text-[#D4A017]" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-white">Community Pulse</p>
                          <p className="text-[10px] text-white/40">What Essex is talking about</p>
                        </div>
                        <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live
                        </span>
                      </div>

                      {/* Real community posts */}
                      <div className="space-y-2.5 mb-5">
                        {recentCommunityPosts.length > 0 ? recentCommunityPosts.map((post) => {
                          const typeColors = { general: '#64748B', question: '#6B8DE3', recommendation: '#5BAA6B', poll: '#C5962E', photo: '#9678CB', 'lost-found': '#D06060', traffic: '#C99430', alert: '#D06060', 'crime-safety': '#A04545', 'parenting-family': '#D08AA0', 'pets-animals': '#9C7BCC', 'food-drink': '#C97040', 'home-garden': '#5BAA6B', 'events-whats-on': '#9678CB', 'weather-travel': '#5A9FBD', 'business-shoutouts': '#C5962E', 'for-sale-free-wanted': '#4A9E7A', 'local-history': '#8B8178', 'rants-raves': '#D06060' };
                          const typeLabels = { general: 'General', question: 'Question', recommendation: 'Recommendation', poll: 'Poll', photo: 'Photo', 'lost-found': 'Lost & Found', traffic: 'Traffic', alert: 'Alert', 'crime-safety': 'Safety', 'parenting-family': 'Family', 'pets-animals': 'Pets', 'food-drink': 'Food & Drink', 'home-garden': 'Home & Garden', 'events-whats-on': 'Events', 'weather-travel': 'Weather', 'business-shoutouts': 'Shout-out', 'for-sale-free-wanted': 'For Sale', 'local-history': 'History', 'rants-raves': 'Rants & Raves' };
                          const pType = post.type || post.post_type || 'general';
                          const color = typeColors[pType] || '#0F2C3E';
                          const postAge = post.created_at ? (() => { const d = Date.now() - new Date(post.created_at).getTime(); const m = Math.floor(d/60000); if (m < 1) return 'just now'; if (m < 60) return `${m}m ago`; const h = Math.floor(m/60); if (h < 24) return `${h}h ago`; const dy = Math.floor(h/24); return `${dy}d ago`; })() : '';
                          return (
                            <div key={post.id} className="bg-[#0F2C3E]/60 backdrop-blur-sm border border-white/10 rounded-xl p-3 hover:bg-[#0F2C3E]/75 transition-colors cursor-pointer" onClick={() => navigate('/community')}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${color}30`, color }}>{typeLabels[pType] || pType}</span>
                                {post.town && <span className="text-[10px] text-white/40">{post.town}</span>}
                                <span className="ml-auto text-[10px] text-white/30">{postAge}</span>
                              </div>
                              <p className="text-xs text-white/80 leading-relaxed line-clamp-2">{(() => { const txt = (post.content || '').replace(/<[^>]*>/g, ''); const el = document.createElement('textarea'); el.innerHTML = txt; return el.value; })()}</p>
                              {(post.display_name || post.user_name) && <p className="text-[10px] text-white/40 mt-1.5">by {post.display_name || post.user_name}</p>}
                            </div>
                          );
                        }) : [
                          { tag: 'Traffic', text: 'A12 delays near Chelmsford — anyone know what happened?', color: '#C99430', author: 'Sarah J.', time: '2h ago' },
                          { tag: 'Recommendation', text: 'Best fish & chips in Leigh-on-Sea? Go!', color: '#5BAA6B', author: 'Mark T.', time: '4h ago' },
                          { tag: 'Lost & Found', text: 'Missing tabby cat near Brentwood High Street', color: '#D06060', author: 'Emma K.', time: '6h ago' },
                        ].map((item, i) => (
                          <div key={i} className="bg-[#0F2C3E]/60 backdrop-blur-sm border border-white/10 rounded-xl p-3 hover:bg-[#0F2C3E]/75 transition-colors cursor-pointer" onClick={() => navigate('/community')}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${item.color}30`, color: item.color }}>{item.tag}</span>
                              <span className="ml-auto text-[10px] text-white/30">{item.time}</span>
                            </div>
                            <p className="text-xs text-white/80 leading-relaxed">{item.text}</p>
                            <p className="text-[10px] text-white/40 mt-1.5">by {item.author}</p>
                          </div>
                        ))}
                      </div>

                      <button onClick={() => navigate('/community')} className="w-full py-2.5 rounded-xl border border-[#D4A017]/40 text-[#D4A017] text-sm font-semibold hover:bg-[#D4A017]/10 transition-all flex items-center justify-center gap-1.5" data-testid="hero-community-link">
                        <MessageSquare className="h-3.5 w-3.5" /> See all community posts
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : heroConfig?.layout === 'search_card' ? (
          /* ═══════════════════════════════════════════
             SEARCH + CARD LAYOUT — Two-column with business sign-up card
          ═══════════════════════════════════════════ */
          <>
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute -top-32 -left-32 w-[600px] h-[600px] bg-[#0F2C3E]/30 rounded-full blur-[140px]" />
              <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#D4A017]/8 rounded-full blur-[120px]" />
            </div>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-24 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">

                {/* LEFT — Search + headline */}
                <div className="col-span-1 lg:col-span-7 flex flex-col gap-5">
                  {/* Badge — matches community layout style */}
                  <div>
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white/90 text-sm font-medium" data-testid="hero-badge">
                      <Sparkles className="h-3.5 w-3.5 text-[#D4A017]" />
                      {heroConfig?.badge_text || "Essex's Premier Local Directory"}
                    </span>
                  </div>

                  {/* Heading — matches community layout: Outfit font-black */}
                  <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight" data-testid="hero-heading">
                    {heroConfig?.heading_prefix || 'Discover Local Businesses, Jobs & Events in'}{' '}
                    <span className="text-[#D4A017]">{heroConfig?.heading_highlight || 'Essex'}</span>
                  </h1>

                  <p className="text-base md:text-lg text-white/80 leading-relaxed font-light">
                    {heroSubtext}
                  </p>

                  {/* WHAT + WHERE search bar */}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const params = new URLSearchParams();
                      if (searchQuery.trim()) params.set('q', searchQuery.trim());
                      if (heroSearchWhere) params.set('town', heroSearchWhere);
                      navigate(`/search?${params.toString()}`);
                    }}
                    className="relative z-20"
                    data-testid="search-form"
                    ref={heroSearchRef}
                  >
                    <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 flex flex-col sm:flex-row overflow-visible transition-all duration-300 focus-within:shadow-[0_0_0_3px_rgba(212,160,23,0.3)]">
                      {/* WHAT */}
                      <div className="flex-1 flex items-center px-4 py-1 sm:border-r border-slate-200/60">
                        <Search className="h-4 w-4 text-slate-400 mr-2.5 shrink-0" />
                        <div className="flex flex-col flex-1 py-1.5">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none mb-0.5">What</span>
                          <input
                            type="text"
                            placeholder="Plumber, café, salon..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => setShowSuggestions(true)}
                            className="bg-transparent border-0 outline-none text-sm text-slate-800 placeholder:text-slate-400 w-full"
                            data-testid="search-input"
                          />
                        </div>
                      </div>
                      {/* WHERE — town dropdown */}
                      <div className="flex-1 flex items-center px-4 py-1 border-t sm:border-t-0 border-slate-200/60">
                        <MapPin className="h-4 w-4 text-slate-400 mr-2.5 shrink-0" />
                        <div className="flex flex-col flex-1 py-1.5">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none mb-0.5">Where</span>
                          <select
                            value={heroSearchWhere}
                            onChange={(e) => setHeroSearchWhere(e.target.value)}
                            className="bg-transparent border-0 outline-none text-sm text-slate-800 w-full cursor-pointer appearance-none"
                            data-testid="search-where-select"
                          >
                            <option value="">All locations</option>
                            {heroTowns.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="p-2">
                        <Button type="submit" className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 text-white h-11 px-6 rounded-xl font-medium w-full sm:w-auto" data-testid="search-btn">Search</Button>
                      </div>
                    </div>
                    {showSuggestions && searchSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border z-[100] overflow-hidden max-h-72 overflow-y-auto">
                        {searchSuggestions.map((listing) => (
                          <button key={listing.id} onClick={() => handleSuggestionClick(listing.id)} className="w-full px-4 py-3 hover:bg-slate-50 text-left flex items-center gap-3 transition-colors">
                            <MapPin className="h-4 w-4 text-[#0F2C3E]" />
                            <div className="flex-1"><div className="font-medium text-sm">{listing.title}</div><div className="text-xs text-slate-500">{listing.town} · {listing.category}</div></div>
                          </button>
                        ))}
                      </div>
                    )}
                  </form>

                  <PopularCategories tone="dark" />
                </div>

                {/* RIGHT — Business sign-up card (no form inputs — sign up first) */}
                <div className="col-span-1 lg:col-span-5 mt-2 lg:mt-8">
                  <div className="bg-white rounded-2xl shadow-2xl p-7 border border-white/80" data-testid="hero-business-card">
                    <span className="inline-block px-3 py-1 bg-[#D4A017]/15 border border-[#D4A017]/30 text-[#D4A017] text-[11px] font-bold uppercase tracking-widest rounded-full mb-4">
                      {heroConfig?.card_badge || 'For Businesses'}
                    </span>
                    <h2 className="text-xl font-bold text-[#0F2C3E] leading-snug mb-4">
                      {heroConfig?.card_title || 'Own a business? List it free.'}
                    </h2>
                    <ul className="space-y-2.5 mb-7">
                      {[
                        heroConfig?.card_bullet_1 || 'Free to list — no credit card, no contract',
                        heroConfig?.card_bullet_2 || 'Get found by locals actively searching for your services',
                        heroConfig?.card_bullet_3 || 'Collect reviews that build trust with new customers',
                        heroConfig?.card_bullet_4 || 'Respond to service requests and win new work',
                        heroConfig?.card_bullet_5 || 'Boost your Google visibility with a trusted local profile',
                      ].map((bullet, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                          <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#4A6741]/15 flex items-center justify-center">
                            <CheckCircle className="h-3.5 w-3.5 text-[#4A6741]" />
                          </span>
                          {bullet}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => { axios.post(`${API}/track-click`, { element: 'hero_card_cta', cta_text: heroConfig?.card_cta_text || "Get Listed — It's Free", cta_url: user ? '/create-listing' : '/signup' }).catch(() => {}); navigate(user ? '/create-listing' : '/signup'); }}
                      className="w-full bg-[#D4A017] hover:bg-[#c4930f] text-white font-bold py-3.5 rounded-xl text-sm transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      data-testid="hero-card-cta-btn"
                    >
                      {heroConfig?.card_cta_text || "Get Listed — It's Free"}
                      <ArrowUpRight className="h-4 w-4" />
                    </button>
                    <p className="text-center text-xs text-slate-500 mt-3">
                      Already listed?{' '}
                      <button onClick={() => navigate('/login')} className="text-[#0F2C3E] font-semibold hover:underline">
                        Log in to manage your listing.
                      </button>
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </>
        ) : (
          <>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4A017] rounded-full blur-3xl" />
            </div>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-28 md:py-36 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <div className="inline-block mb-6">
                  <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium" data-testid="hero-badge">
                    {heroConfig?.badge_text || "Essex's Premier Local Directory"}
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight" data-testid="hero-heading">
                  {heroConfig?.heading_prefix || 'Local Businesses, Jobs & Events in'}{' '}
                  <span className="text-[#D4A017]">{heroConfig?.heading_highlight || 'Essex'}</span>
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  {heroSubtext}
                </p>
                <form onSubmit={handleSearch} className="max-w-2xl mx-auto relative z-20" data-testid="search-form" ref={heroSearchRef}>
                  <div className="glass flex gap-2 rounded-2xl p-2 shadow-2xl">
                    <div className="relative flex-1">
                      <Input
                        type="text"
                        placeholder="Search businesses & more"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setShowSuggestions(true)}
                        className="search-input border-0 focus-visible:ring-0 text-base h-12 bg-transparent"
                        data-testid="search-input"
                      />
                      {showSuggestions && searchSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border z-[100] overflow-hidden max-h-80 overflow-y-auto">
                          {searchSuggestions.map((listing) => (
                            <button key={listing.id} onClick={() => handleSuggestionClick(listing.id)} className="w-full px-4 py-3 hover:bg-slate-50 text-left flex items-center gap-3 transition-colors">
                              <MapPin className="h-4 w-4 text-[#0F2C3E]" />
                              <div className="flex-1"><div className="font-medium">{listing.title}</div><div className="text-sm text-slate-500">{listing.town} • {listing.category}</div></div>
                              <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">{listing.listing_type === 'classified' ? 'Marketplace' : listing.listing_type}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button type="submit" className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 px-8 h-12 rounded-xl btn-primary" data-testid="search-btn">
                      <Search className="h-5 w-5 mr-2" />Search
                    </Button>
                  </div>
                </form>
                <div className="mt-8 text-center animate-fade-in-up relative z-10" data-testid="hero-free-listing-cta">
                  <p className="text-white/80 text-sm mb-3">{heroCtaSubtext || 'All listing types are completely free to create — no credit card required'}</p>
                  <button
                    onClick={() => { axios.post(`${API}/track-click`, { element: 'hero_get_started', headline: 'Hero — Get Started Free', subtext: 'Homepage', cta_text: heroConfig?.cta_text || "Get Started — It's Free", cta_url: '/create-listing' }).catch(() => {}); navigate(user ? '/create-listing' : '/signup'); }}
                    className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#c4930f] text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                    data-testid="hero-get-started-btn"
                  >
                    {heroConfig?.cta_text || "Get Started — It's Free"}<ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Quick "Find a Trade" strip — removed ── */}

      </section>

      {/* Activity Ticker */}
      <ActivityTicker />

      {/* Top Banner Ad */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4">
        <TopBannerAd />
      </div>

      {/* Social Proof + Urgency Section */}
      {!user && (
        <section className="py-14 bg-gradient-to-b from-slate-50 to-white reveal" data-testid="social-proof-section">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm font-medium px-4 py-1.5 rounded-full mb-4" data-testid="free-to-join-pill">
                <CheckCircle className="h-3.5 w-3.5" />
                Free to join — no credit card required
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3">
                Everything Essex, in <span className="text-[#4A6741]">one account</span>
              </h2>
              <p className="text-slate-600 max-w-xl mx-auto text-base">
                {stats.signups_week > 9 ? `${stats.signups_week} new members joined this week — ` : ''}discover local businesses, events, jobs and marketplace deals with a single sign-up.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2.5 sm:gap-x-8 sm:gap-y-3 max-w-2xl mx-auto mb-8">
              {[
                { icon: Search, text: 'Find trusted local businesses & tradespeople', color: '#0F2C3E' },
                { icon: Calendar, text: 'Stay updated with Essex events & job openings', color: '#4A6741' },
                { icon: HandHelping, text: 'Post a request and get quotes in hours', color: '#D4A017' },
                { icon: Tag, text: 'Buy, sell & trade in the local marketplace', color: '#8B5E3C' },
                { icon: Building2, text: 'List your own business for free exposure', color: '#2563EB' },
                { icon: Star, text: 'Leave reviews and help your community', color: '#7C3AED' },
              ].map(({ icon: Icon, text, color }) => (
                <div key={text} className="flex items-start gap-2.5 p-3 sm:p-2 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-0">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}10` }}>
                    <Icon className="h-4 w-4" style={{ color }} />
                  </div>
                  <span className="text-slate-700 text-xs sm:text-sm leading-snug pt-1">{text}</span>
                </div>
              ))}
            </div>

            <div className="bg-[#0F2C3E] rounded-2xl p-6 sm:p-8 text-center">
              <p className="text-[#D4A017] text-xs font-semibold uppercase tracking-widest mb-2">Early Member Perk</p>
              <p className="text-white text-base sm:text-lg font-medium mb-4">
                Join now and get <span className="text-[#D4A017] font-bold">priority placement</span> as Discover Essex grows across the county.
              </p>
              <Link to="/signup">
                <button className="bg-[#D4A017] hover:bg-[#b8891a] text-[#0F2C3E] font-bold px-8 py-3.5 rounded-xl transition-all duration-200 text-base pulse-cta">
                  Join Free — It Takes 30 Seconds
                </button>
              </Link>
              <p className="text-white/50 text-xs mt-3">No credit card required. No spam. Unsubscribe anytime.</p>
            </div>
          </div>
        </section>
      )}

      {/* Browse by Category section removed - may return later */}

      {/* Why Join Discover Essex — only shown to non-logged-in users */}
      {!user && (
      <section className="py-16 bg-[#0F2C3E] reveal" data-testid="why-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#D4A017] text-sm font-semibold uppercase tracking-widest mb-3">Free to Join</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Join Discover Essex?</h2>
            <p className="text-slate-300 max-w-2xl mx-auto text-base">
              Whether you're a local resident, a business owner, or someone looking for what Essex has to offer — there's something here for you.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 reveal-stagger">
            {[
              {
                icon: Search,
                title: 'Find Trusted Local Services',
                desc: 'Discover verified businesses, tradespeople and services near you — with reviews from real Essex residents.',
                color: 'bg-blue-500/10 text-blue-300',
                border: 'border-blue-500/20'
              },
              {
                icon: Calendar,
                title: 'Discover Events & Opportunities',
                desc: 'Stay in the loop with local events, job openings and marketplace listings across Essex — all in one place.',
                color: 'bg-[#D4A017]/10 text-[#D4A017]',
                border: 'border-[#D4A017]/20'
              },
              {
                icon: HandHelping,
                title: 'Get Help When You Need It',
                desc: 'Post a "Find a Trade" request and let local professionals come to you with quotes and availability.',
                color: 'bg-emerald-500/10 text-emerald-300',
                border: 'border-emerald-500/20'
              },
              {
                icon: Building2,
                title: 'Promote Your Business Free',
                desc: 'List your business, post jobs, or advertise events — completely free with no hidden fees or subscriptions.',
                color: 'bg-purple-500/10 text-purple-300',
                border: 'border-purple-500/20'
              }
            ].map(({ icon: Icon, title, desc, color, border }) => (
              <div key={title} className={`reveal rounded-2xl border ${border} bg-white/5 p-6 flex flex-col gap-4 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-base mb-1">{title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to={user ? '/create-listing' : '/signup'}>
              <button className="bg-[#D4A017] hover:bg-[#b8891a] text-[#0F2C3E] font-bold px-8 py-3.5 rounded-xl transition-all duration-200 text-base pulse-cta">
                {user ? 'Add a Listing' : 'Join Free Today'}
              </button>
            </Link>
            <Link to="/business">
              <button className="border border-white/20 text-white hover:bg-white/10 font-medium px-8 py-3.5 rounded-xl transition-colors text-base">
                Browse Listings
              </button>
            </Link>
          </div>
        </div>
      </section>
      )}

      {featuredListings.length > 0 ? (
        <section className="py-16 bg-slate-50 reveal" data-testid="featured-section">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4">Featured Listings</h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Discover top-rated businesses, upcoming events and exciting opportunities
              </p>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 reveal-stagger">
              {featuredListings.map((listing, index) => (
                <div key={listing.id} className={`reveal relative ${index >= 12 ? 'lg:hidden' : ''}`}>
                  <ListingCard listing={listing} uniformAspect />
                  <button
                    onClick={(e) => toggleWatchlist(listing.id, e)}
                    disabled={watchlistLoading.has(listing.id)}
                    className={`absolute top-3 right-3 z-10 p-2 rounded-full shadow-md transition-all ${
                      watchlist.has(listing.id) ? 'bg-red-50 text-red-500' : 'bg-white/90 text-slate-400 hover:text-red-500 hover:bg-white'
                    }`}
                    data-testid={`home-watchlist-${listing.id}`}
                  >
                    <Heart className={`h-4 w-4 ${watchlist.has(listing.id) ? 'fill-current' : ''}`} />
                  </button>
                </div>
              ))}
            </div>

            {/* Free Listing CTA */}
            <div className="mt-10" data-testid="free-listing-cta-section">
              <div className="bg-[#0F2C3E] rounded-2xl px-6 md:px-8 py-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4 text-white">
                    <div className="hidden sm:flex h-10 w-10 rounded-full bg-white/10 items-center justify-center flex-shrink-0">
                      <Plus className="h-5 w-5 text-[#D4A017]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">List your business, job, event or marketplace ad — completely free</h3>
                      <p className="text-sm text-slate-300 mt-0.5">No hidden fees. Get seen by thousands of Essex residents today.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      axios.post(`${API}/track-click`, {
                        element: 'free_listing_cta',
                        headline: 'List your business, job, event or marketplace ad — completely free',
                        subtext: 'Homepage',
                        cta_text: 'Create a Free Listing',
                        cta_url: '/create-listing'
                      }).catch(() => {});
                      navigate(user ? '/create-listing' : '/signup');
                    }}
                    className="flex-shrink-0 inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#c4930f] text-white px-6 py-3 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap"
                    data-testid="free-listing-cta-btn"
                  >
                    Create a Free Listing
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-16 bg-slate-50" data-testid="featured-skeleton">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="h-8 w-64 bg-slate-200 rounded-lg mx-auto mb-4 animate-pulse" />
              <div className="h-4 w-96 bg-slate-200 rounded mx-auto animate-pulse" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm animate-pulse">
                  <div className="h-48 bg-slate-200" />
                  <div className="p-4 space-y-3">
                    <div className="h-5 w-3/4 bg-slate-200 rounded" />
                    <div className="h-4 w-1/2 bg-slate-200 rounded" />
                    <div className="h-3 w-1/3 bg-slate-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SEO Content Section */}
      <section className="py-16 bg-white reveal" data-testid="home-seo-content">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-800 [&_h2]:mt-0 [&_h2]:mb-4 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-slate-700 [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:mb-4 [&_a]:text-[#0F2C3E] [&_a]:underline [&_a]:decoration-slate-300 hover:[&_a]:decoration-[#0F2C3E] [&_a]:transition-colors">
              
              <h2>Your Local Directory for Essex — Connecting Communities Since Day One</h2>
              <p>
                Discover Essex is the county's dedicated online directory, built to connect residents, businesses, and visitors with everything Essex has to offer. Whether you are searching for a trusted tradesperson in <Link to="/town/chelmsford">Chelmsford</Link>, browsing <Link to="/jobs">job vacancies</Link> in <Link to="/town/colchester">Colchester</Link>, or looking for weekend <Link to="/events">events</Link> near <Link to="/town/southend-on-sea">Southend-on-Sea</Link>, our platform brings together thousands of local listings in one place. We believe that the best way to support a thriving county is to make it easier for people to find, hire, and engage with the businesses and services on their doorstep.
              </p>

              <h3>Local Businesses Across Every Corner of Essex</h3>
              <p>
                Essex is home to an extraordinary range of independent businesses, from family-run cafes and specialist retailers to established tradespeople and professional service firms. Our <Link to="/business">business directory</Link> covers every category you might need — plumbers, electricians, accountants, hairdressers, dog walkers, personal trainers, web designers, and many more. Each listing includes contact details, customer reviews, and a description of services offered, making it straightforward to compare providers and choose with confidence. Towns like <Link to="/town/brentwood">Brentwood</Link>, <Link to="/town/basildon">Basildon</Link>, and <Link to="/town/braintree">Braintree</Link> are particularly well represented, but you will find active listings in communities right across the county, from <Link to="/town/harwich">Harwich</Link> on the north-east coast to <Link to="/town/grays">Grays</Link> in the south.
              </p>

              <h3>Job Opportunities and Career Growth in Essex</h3>
              <p>
                The Essex economy is diverse and resilient, with strong sectors in technology, healthcare, construction, logistics, retail, and the creative industries. Our <Link to="/jobs">jobs board</Link> features vacancies posted directly by local employers who are actively hiring. Unlike national job sites, every role listed here is based in Essex, which means less time filtering and more time applying. Whether you are a school leaver looking for an apprenticeship, a professional seeking a senior position, or someone returning to work after a break, you will find opportunities in towns such as <Link to="/town/harlow">Harlow</Link>, <Link to="/town/witham">Witham</Link>, and <Link to="/town/rayleigh">Rayleigh</Link>. Employers benefit too — listing a vacancy on Discover Essex puts your opening in front of a genuinely local audience, reducing recruitment costs and improving candidate quality.
              </p>

              {/* Remaining SEO content — hidden visually but present in DOM for crawlers */}
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${showMoreSeo ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}
                aria-hidden={!showMoreSeo}
              >
              <h3>Events, Activities, and Things to Do</h3>
              <p>
                One of the great strengths of Essex is its vibrant social calendar. From summer music festivals and Christmas markets to weekly parkruns, charity quizzes, and art exhibitions, there is always something happening nearby. Our <Link to="/events">events page</Link> lets you search by date, location, and category, so you can plan your weekends around what matters to you. Coastal towns like <Link to="/town/clacton-on-sea">Clacton-on-Sea</Link> and <Link to="/town/leigh-on-sea">Leigh-on-Sea</Link> host seasonal seafront events, while market towns such as <Link to="/town/saffron-walden">Saffron Walden</Link> and <Link to="/town/maldon">Maldon</Link> are known for their traditional fairs and food festivals. If you are organising an event, <Link to="/create-listing">adding your listing</Link> takes just a few minutes and is an effective way to reach a local audience.
              </p>

              <h3>Marketplace — Buy, Sell, and Trade Locally</h3>
              <p>
                Our <Link to="/marketplace">marketplace</Link> provides a simple, community-focused platform for buying, selling, and trading goods across Essex. You will find everything from furniture and electronics to vehicles, clothing, garden equipment, and children's toys. Buying locally means you can inspect items before committing, avoid delivery charges, and reduce your environmental impact. Sellers can create a listing for free, upload photographs, and reach buyers in their immediate area. Residents across the county, from <Link to="/town/billericay">Billericay</Link> and <Link to="/town/wickford">Wickford</Link> to <Link to="/town/epping">Epping</Link> and <Link to="/town/loughton">Loughton</Link>, use our marketplace as a practical alternative to larger online platforms.
              </p>

              <h3>Find a Trade Let Local Professionals Come to You</h3>
              <p>
                Sometimes you know what you need done but are not sure who to call. Our <Link to="/find-a-trade">Find a Trade</Link> feature turns the traditional directory model around — you describe the job, set your budget, and let local service providers respond with quotes and availability. It is a fast, transparent way to find help with anything from emergency plumbing and garden clearance to decorating, removals, and specialist cleaning. Businesses across Essex monitor these requests daily, so you can expect competitive offers from qualified professionals in your area.
              </p>

              <h3>List Your Business for Free — No Catch, No Hidden Fees</h3>
              <p>
                One of the things that sets Discover Essex apart from other directories is that listing your business, job, event, or marketplace ad is completely free. There is no trial period, no credit card required, and no limit on how long your listing stays live. We made this decision deliberately because we believe that every business in Essex — whether it is a sole trader just starting out or an established firm with decades of experience — deserves to be visible online without facing a financial barrier. Too many directories charge monthly subscriptions simply to appear in search results, which prices out the smallest businesses that often need exposure the most.
              </p>
              <p>
                Creating a free listing takes just a few minutes. You can add your business name, description, contact details, category, photographs, and the town you serve. Your listing will appear in our directory, in the relevant category page, on your <Link to="/town/chelmsford">town page</Link>, and in search results — all at no cost. Customers can leave reviews, which helps you build trust and credibility over time. For businesses that want additional visibility, we also offer <Link to="/pricing">optional premium plans</Link> that include features such as featured placement, priority in search results, and enhanced listing profiles. But the core listing is — and always will be — free. <Link to="/create-listing">Add your listing today</Link> and start reaching customers across Essex.
              </p>

              <h3>Why Choose Discover Essex?</h3>
              <p>
                There are many online directories, but Discover Essex is different because it is built exclusively for this county. Every listing, every job, and every event on our platform is based in Essex. That focus means better search results, more relevant recommendations, and a stronger sense of community. Whether you are a long-time resident of <Link to="/town/romford">Romford</Link> or recently moved to <Link to="/town/burnham-on-crouch">Burnham-on-Crouch</Link>, Discover Essex is your starting point for local life. Essex spans a remarkable variety of landscapes and communities, from the commuter suburbs of west Essex through to the unspoilt marshlands of the Dengie peninsula and the sandy beaches of the Sunshine Coast. Each of our <strong>33 town pages</strong> provides a dedicated guide to what is available locally, including businesses, jobs, events, and marketplace listings filtered to that specific area.
              </p>
              </div>

              {!showMoreSeo && (
                <button
                  onClick={() => setShowMoreSeo(true)}
                  className="mt-2 text-sm font-medium text-[#0F2C3E] hover:underline cursor-pointer inline-flex items-center gap-1"
                  data-testid="seo-read-more"
                >
                  Read more
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Popular Services - Micro-Niche Internal Links */}
      {popularPages.length > 0 && (
        <section className="py-12 bg-slate-50" data-testid="popular-services-section">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-semibold text-[#0F2C3E] mb-2">Popular Services by Town</h2>
              <p className="text-sm text-slate-500 mb-5">Find local providers in your area</p>
              <div className="flex flex-wrap gap-2">
                {popularPages.map((page, idx) => (
                  <Link key={`${page.url}-${idx}`} to={page.url}>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-white border border-slate-200 text-slate-700 hover:border-[#0F2C3E]/30 hover:text-[#0F2C3E] transition-colors cursor-pointer">
                      {page.label}
                      <span className="text-xs text-slate-400">({page.count})</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Facebook Group CTA */}
      <section className="bg-white" data-testid="fb-group-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <FacebookGroupCTA page="Homepage" />
        </div>
      </section>

      {/* Footer Ad Banner - above newsletter */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4">
        <FooterAd />
      </div>

      <NewsletterSection />

      <SignUpPromptModal isOpen={showSignUpModal} onClose={() => setShowSignUpModal(false)} variant="watchlist" />
    </div>
  );
};

export default Home;